/*
 * Copyright (c) 2023, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */
import React from 'react';
import {
  Box,
  FormHelperText,
  OutlinedInput,
  PopoverOrigin,
  Select,
  SelectProps,
} from '@material-ui/core';
import clsx from 'clsx';
import CircularLoading from 'components/CircularLoading/CircularLoading';
import { ChevronDown, Info } from '../Icons/generated';
import useStyles from './SimpleSelect.styles';

interface SimpleSelectProps
  extends Omit<
    SelectProps,
    'onChange' | 'value' | 'placeholder' | 'renderValue'
  > {
  testId: string;
  onChange: (event: React.ChangeEvent<{ value: unknown }>) => void;
  value: unknown;
  placeholder?: string;
  error?: boolean;
  size?: 'small' | 'medium';
  children: React.ReactNode;
  helperText?: React.ReactNode;
  renderValue?: SelectProps['renderValue'];
  resetStyles?: boolean;
  anchorOrigin?: PopoverOrigin;
  transformOrigin?: PopoverOrigin;
  isLoading?: boolean;
  isScrollable?: boolean;
}

function SimpleSelect(props: SimpleSelectProps) {
  const {
    testId,
    children,
    value,
    size = 'medium',
    error = false,
    placeholder,
    helperText,
    resetStyles = false,
    anchorOrigin = {
      vertical: 'bottom',
      horizontal: 'left',
    },
    transformOrigin,
    renderValue,
    displayEmpty,
    onChange,
    disabled,
    isLoading,
    isScrollable = false,
    startAdornment,
  } = props;
  const classes = useStyles();

  const CircularLoader = () => (
    <Box className={classes.loadingIcon}>
      <CircularLoading />
    </Box>
  );

  const isEmptySelection = (selectedValue: unknown): boolean =>
    selectedValue === '' ||
    selectedValue === undefined ||
    selectedValue === null ||
    (Array.isArray(selectedValue) && selectedValue.length === 0);

  const defaultRenderValue = (selectedValue: unknown): React.ReactNode => {
    if (isEmptySelection(selectedValue)) {
      return <span className={classes.placeholder}>{placeholder}</span>;
    }

    return selectedValue as React.ReactNode;
  };

  const resolvedRenderValue =
    renderValue || (placeholder ? defaultRenderValue : undefined);

  return (
    <Box
      className={clsx({
        [classes.simpleSelect]: true,
        [classes.resetSimpleSelectStyles]: resetStyles,
      })}
    >
      <Select
        startAdornment={startAdornment}
        disabled={disabled || isLoading}
        data-cyid={testId}
        data-testid={testId}
        value={value}
        onChange={onChange}
        displayEmpty={displayEmpty || Boolean(placeholder)}
        IconComponent={isLoading ? CircularLoader : ChevronDown}
        variant="outlined"
        input={<OutlinedInput notched={false} label="" />}
        MenuProps={{
          PopoverClasses: {
            paper: `${classes.listPaper} ${
              isScrollable ? classes.scrollableList : ''
            } ${startAdornment ? classes.startAdornmentAlignLeft : ''}`,
          },
          getContentAnchorEl: null,
          anchorOrigin,
          transformOrigin,
        }}
        renderValue={resolvedRenderValue}
        error={error}
        fullWidth
        classes={{
          root: clsx({
            [classes.root]: true,
            [classes.rootSmall]: size === 'small',
            [classes.rootMedium]: size === 'medium',
          }),
          select: classes.select,
          selectMenu: classes.selectMenu,
          disabled: classes.disabled,
          icon: clsx({
            [classes.icon]: true,
            [classes.iconSmall]: size === 'small',
            [classes.iconMedium]: size === 'medium',
          }),
          outlined: clsx({
            [classes.outlined]: true,
            [classes.outlinedSmall]: size === 'small',
            [classes.outlinedMedium]: size === 'medium',
          }),
        }}
      >
        {children}
      </Select>
      {helperText && (
        <FormHelperText error={error}>
          <Box display="flex" alignItems="center">
            {error && (
              <Box className={classes.selectInfoIcon}>
                <Info fontSize="inherit" />
              </Box>
            )}
            <Box ml={1}>{helperText}</Box>
          </Box>
        </FormHelperText>
      )}
    </Box>
  );
}

export default SimpleSelect;
